import { useState, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { SettingItem } from '../types';
import { logger } from '../services/logger';

export const useFileUpload = (onUploadComplete: () => void) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number, fileProgress: number, fileName: string } | null>(null);
    const [failedUploads, setFailedUploads] = useState<{ file: File; error: string }[]>([]);
    const [error, setError] = useState<string | null>(null);
    
    const handleFilesSelected = useCallback(async (files: File[], tags: SettingItem[] = []) => {
        if (files.length === 0) return;

        setIsProcessing(true);
        setError(null);
        setFailedUploads([]);
        const total = files.length;
        
        try {
            setUploadProgress({
                current: total,
                total: total,
                fileProgress: 50, // Fake progress for the batch
                fileName: `Nahrávám ${total} souborů...`
            });
            await apiService.uploadFilesToQueue(files, tags);

            logger.info(`${total} souborů úspěšně nahráno do fronty.`);
        } catch (err: any) {
             setError(err.message || 'Nahrávání selhalo.');
             setFailedUploads(files.map(file => ({ file, error: 'Nahrávání selhalo' })));
        } finally {
            setUploadProgress(null);
            setIsProcessing(false);
            if (onUploadComplete) {
                onUploadComplete();
            }
        }
    }, [onUploadComplete]);

    const retryFailedUploads = useCallback((tags: SettingItem[] = []) => {
        const filesToRetry = failedUploads.map(f => f.file);
        if(filesToRetry.length > 0) {
            setFailedUploads([]);
            handleFilesSelected(filesToRetry, tags);
        }
    }, [failedUploads, handleFilesSelected]);

    return {
        isProcessing,
        uploadProgress,
        failedUploads,
        setFailedUploads,
        error,
        handleFilesSelected,
        retryFailedUploads,
    };
};
